import React, { useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AverageStatsDisplay from "./AverageStatsDisplay";
import ToolStatePanel from "../../__shared/ToolStatePanel";
import { toChartSeries } from "../scoutingSeries";
import "./TeamOffensiveStatsDisplay.css";

const GET_TEAM_STATS = graphql`
  query TeamOffensiveStatsDisplayQuery($teamId: ID!, $seasonID: ID) {
    nflTeamWeekStats(teamId: $teamId, seasonId: $seasonID) {
      week {
        week
      }

      team {
        city
        abbreviation
      }

      opp {
        city
        abbreviation
      }

      passAtt
      rushAtt

      passYds
      rushYds

      timeOfPos
    }

    nflTeamOpponentWeekStats(teamId: $teamId, seasonId: $seasonID) {
      week {
        week
      }

      team {
        city
        abbreviation
      }

      opp {
        city
        abbreviation
      }

      passAtt
      rushAtt

      passYds
      rushYds

      timeOfPos
    }
  }
`;

const PLAYS_DOMAIN = [
  (dataMin) => Math.floor(dataMin / 10) * 10,
  (dataMax) => Math.ceil(dataMax / 10) * 10 + 10,
];

const PER_PLAY_DOMAIN = [
  (dataMin) => Math.floor(dataMin / 2) * 2 - 2,
  (dataMax) => Math.ceil(dataMax / 2) * 2 + 2,
];

const TOP_DOMAIN = [
  (dataMin) => Math.floor(dataMin / 5) * 5 - 5,
  (dataMax) => Math.ceil(dataMax / 5) * 5 + 5,
];

export default function TeamOffensiveStatsDisplay({ teamId, seasonID, fetchKey }) {
  const data = useLazyLoadQuery(
    GET_TEAM_STATS,
    { teamId, seasonID },
    { fetchKey, fetchPolicy: fetchKey > 0 ? "network-only" : "store-or-network" }
  );

  if (!data.nflTeamWeekStats) {
    throw new Error("nflTeamWeekStats returned null");
  }

  if (!data.nflTeamOpponentWeekStats) {
    throw new Error("nflTeamOpponentWeekStats returned null");
  }

  const series = useMemo(
    () => toChartSeries(data.nflTeamWeekStats, data.nflTeamOpponentWeekStats),
    [data]
  );

  if (series.plays.length === 0) {
    return (
      <ToolStatePanel
        title="No games played yet"
        body="Week-by-week charts show up after this team's first game."
      />
    );
  }

  return (
    <div className="nfl-sr-charts">
      <ChartCard
        title="Offensive plays by week"
        data={series.plays}
        teamLabel="Plays"
        oppLabel="Opp plays"
        domain={PLAYS_DOMAIN}
        averageLabel="Avg plays"
        average={series.averages.plays}
      />
      <ChartCard
        title="Offensive yards per play"
        data={series.yardsPerPlay}
        teamLabel="Yards per play"
        oppLabel="Opp yards per play"
        domain={PER_PLAY_DOMAIN}
        averageLabel="Avg yards / play"
        average={series.averages.yardsPerPlay}
      />
      <ChartCard
        title="Time of possession"
        data={series.timeOfPossession}
        teamLabel="TOP"
        oppLabel="Opp TOP"
        domain={TOP_DOMAIN}
        averageLabel="Avg TOP"
        average={series.averages.timeOfPossession}
      />
    </div>
  );
}

function ChartCard({
  title,
  data,
  teamLabel,
  oppLabel,
  domain,
  averageLabel,
  average,
}) {
  return (
    <section className="nfl-sr-chart-card">
      <h3 className="nfl-sr-chart-title nfl-display">{title}</h3>
      <div className="nfl-sr-chart-body">
        <div className="nfl-sr-chart">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
              <Legend verticalAlign="top" align="right" height={26} />
              <Line
                name={teamLabel}
                type="monotone"
                dataKey="team"
                stroke="var(--nfl-turf-deep)"
                strokeWidth={2}
                dot={false}
              />
              <Line
                name={oppLabel}
                type="monotone"
                dataKey="opp"
                stroke="var(--nfl-ink)"
                strokeWidth={2}
                dot={false}
              />
              <XAxis dataKey="week" tick={{ fontSize: 12 }} />
              <YAxis domain={domain} tick={{ fontSize: 12 }} width={46} />
              <Tooltip />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <AverageStatsDisplay
          label={averageLabel}
          statValue={average.team}
          oppStatValue={average.opp}
        />
      </div>
    </section>
  );
}
